import { router } from '@inertiajs/react';
import classNames from 'classnames';
import * as React from 'react';
import { MouseEventHandler } from 'react';

import { useSession } from '../SessionContext/SessionContext';

import styles from './Button.module.css';

type LinkButtonProps = {
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    variant?: 'default' | 'brand' | 'subtle' | 'outline' | 'text' | 'white';
    style?: 'default' | 'pill';
    disableInertia?: boolean;
    noPadding?: boolean;
    fullWidth?: boolean;
    method?: 'get' | 'post';
    disabled?: boolean;
    noRoundCorners?: boolean;
    replaceHistory?: boolean;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'style'>;

const LinkButton = ({
    size = 'medium',
    variant = 'default',
    style = 'default',
    disableInertia = false,
    noPadding = false,
    fullWidth = false,
    noRoundCorners = false,
    replaceHistory = false,
    href = '#',
    method = 'get',
    disabled,
    target,
    children,
    ...anchorProps
}: LinkButtonProps) => {
    const session = useSession();
    const mergedAnchorProps = {
        ...anchorProps,
        className: classNames(styles.Root, anchorProps.className, {
            [styles.sizeXSmall]: size === 'xsmall',
            [styles.sizeSmall]: size === 'small',
            [styles.sizeMedium]: size === 'medium',
            [styles.sizeLarge]: size === 'large',
            [styles.sizeXlarge]: size === 'xlarge',
            [styles.variantDefault]: variant === 'default',
            [styles.variantBrand]: variant === 'brand',
            [styles.variantSubtle]: variant === 'subtle',
            [styles.variantOutline]: variant === 'outline',
            [styles.variantText]: variant === 'text',
            [styles.variantWhite]: variant === 'white',
            [styles.stylePill]: style === 'pill',
            [styles.noPadding]: noPadding,
            [styles.fullWidth]: fullWidth,
            [styles.noRoundCorners]: noRoundCorners,
        }),
    };

    const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
        mergedAnchorProps.onClick && mergedAnchorProps.onClick(event);

        if (disabled) {
            event.preventDefault();
            return;
        }

        if (disableInertia) {
            return;
        }

        if (target === '_blank') {
            return;
        }

        if (event.metaKey) {
            return;
        }

        event.preventDefault();

        router.visit(href, {
            method: method,
            replace: replaceHistory,
            data: method === 'get' ? {} : { tenantId: session.tenantId },
        });
    };

    return (
        <a
            {...mergedAnchorProps}
            href={disabled ? '#' : href}
            onClick={handleClick}
            target={target}
            data-disabled={disabled}
        >
            {children}
        </a>
    );
};

export default LinkButton;
